import { Modal, Tag } from 'antd';
import React, {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import styles from './index.module.scss';
import LeftBox from './components/LeftBox';
import RightBox from './components/RightBox';
import classNames from 'classnames';
import CenterTopBox from './components/CenterTopBox';
import CenterBottomBox from './components/CenterBottomBox';
import {
  apiV2ElectricMapDataScalePost,
  apiV2EnergyOverviewConfigDetailPost,
  V2EnergyOverviewConfigDetailPostResponse,
  V2ElectricMapDataScalePostResponse,
  apiV2EnergyOverviewCostPost,
  V2EnergyOverviewCostPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { useRequest } from 'ahooks';
import { ShareDataWrapperContext } from '../..';
import { IndexTimeActive } from './components/LeftBox/components/SwitchDate';
import dayjs, { Dayjs } from 'dayjs';
import { getDefaultTime } from '../../utils';
import { isNil } from 'lodash-es';
import { UnityClickDetail } from '../UnityRenderMap';
import EnergyOverviewModal from './components/EnergyOverviewModal';
import { useSharedUnityMap } from '../SharedUnityMapLayer';
import MapView from './components/MapView';
import UnityMap from './components/UnityMap';
import type { WgqMapMode } from '../..';
import MapPointModal, { formatCoordinate, MapCoordinate } from './components/MapPointModal';

interface ShareDataProps {
  scaleData?: V2ElectricMapDataScalePostResponse;
  timeResolution?: IndexTimeActive;
  dateValue?: Dayjs;
  setValue?: React.Dispatch<React.SetStateAction<Dayjs | undefined>>;
  setActiveKey?: React.Dispatch<React.SetStateAction<IndexTimeActive>>;
  configDetail?: V2EnergyOverviewConfigDetailPostResponse;
  energyCostData?: V2EnergyOverviewCostPostResponse;
  energyCostDataLoading?: boolean;
  energyUsageExpanded?: boolean;
  setEnergyUsageExpanded?: React.Dispatch<React.SetStateAction<boolean>>;
  energyUsageContentVisible?: boolean;
  overviewRegionName?: string;
}

export const ShareDataUnityEnergyCockpitMapContext = createContext<ShareDataProps>({});

const getMapCoordinate = (data?: Record<string, any>): MapCoordinate | undefined => {
  if (!data) return undefined;

  const source = data.lngLat ?? data.coordinate ?? data.coordinates ?? data.location ?? data;
  const lng = Array.isArray(source) ? source[0] : source.lng ?? source.longitude ?? source.pointLng;
  const lat = Array.isArray(source) ? source[1] : source.lat ?? source.latitude ?? source.pointLat;
  const longitude = Number(lng);
  const latitude = Number(lat);

  return Number.isFinite(longitude) && Number.isFinite(latitude) ? { lng: longitude, lat: latitude } : undefined;
};

const UnityEnergyCockpitMap: React.FC<{ mapMode?: WgqMapMode }> = ({ mapMode = 'unityMap' }) => {
  const { isFullScreen, isStandardSize, loadMap } = useContext(ShareDataWrapperContext);
  const { registerUnityClickHandler } = useSharedUnityMap();
  // /power/monitoring/unityMap 使用 mapView；仅该路由提供坐标拾取。
  const enableCoordinatePicker = mapMode === 'mapView';

  const [activeKey, setActiveKey] = useState<IndexTimeActive>(IndexTimeActive.THIS_MONTH);
  const [value, setValue] = useState<Dayjs>();
  const [energyUsageExpanded, setEnergyUsageExpanded] = useState(false);
  const [energyUsageContentVisible, setEnergyUsageContentVisible] = useState(false);
  const [overviewModalOpen, setOverviewModalOpen] = useState(false);
  const [overviewRegionName, setOverviewRegionName] = useState('');
  const [sidebarTransitionEnabled, setSidebarTransitionEnabled] = useState(false);

  const isLargeMapLayout = !isStandardSize && loadMap;

  // useSize 首次返回空尺寸，随后才会切换到大屏布局。先让侧栏以最终宽度完成首帧渲染，
  // 再恢复用户手动展开/收起时需要的过渡，避免初始化时出现 880px → 440px 的收起动画。
  useLayoutEffect(() => {
    setSidebarTransitionEnabled(false);
    if (!isLargeMapLayout) return;

    let nextFrameId: number | undefined;
    const frameId = window.requestAnimationFrame(() => {
      nextFrameId = window.requestAnimationFrame(() => setSidebarTransitionEnabled(true));
    });

    return () => {
      window.cancelAnimationFrame(frameId);
      if (nextFrameId !== undefined) window.cancelAnimationFrame(nextFrameId);
    };
  }, [isLargeMapLayout]);

  // 展开时，等侧栏宽度动画结束后再挂载“能源用量”内容，避免 Empty 先以完整宽度闪现。
  // 收起时则在浏览器绘制前卸载内容。
  useLayoutEffect(() => {
    if (!isLargeMapLayout) {
      setEnergyUsageContentVisible(true);
      return;
    }

    if (!energyUsageExpanded) {
      setEnergyUsageContentVisible(false);
      return;
    }

    if (!sidebarTransitionEnabled) {
      setEnergyUsageContentVisible(true);
    }
  }, [energyUsageExpanded, isLargeMapLayout, sidebarTransitionEnabled]);

  const handleLeftSidebarTransitionEnd = useCallback(
    (event: React.TransitionEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget && event.propertyName === 'width' && energyUsageExpanded) {
        setEnergyUsageContentVisible(true);
      }
    },
    [energyUsageExpanded]
  );
  const [coordinate, setCoordinate] = useState<MapCoordinate>();
  const [pointModalOpen, setPointModalOpen] = useState(false);

  useEffect(() => {
    // 如果当前是1号，则默认时间为上个月
    const date = dayjs().date() === 1 ? dayjs().subtract(1, 'day') : dayjs();
    setValue(date);
  }, []);

  // 查询配置
  const { data: configDetail } = useRequest(async () => {
    return await apiV2EnergyOverviewConfigDetailPost({});
  });

  const { data: scaleData } = useRequest(() =>
    apiV2ElectricMapDataScalePost({}).then(res => {
      return res;
    })
  );

  // 能源成本
  const { data: energyCostData, loading: energyCostDataLoading } = useRequest(
    async () => {
      return await apiV2EnergyOverviewCostPost({
        timeResolution: activeKey === IndexTimeActive.THIS_MONTH ? 'DAY' : 'MONTH',
        fromTime: getDefaultTime(activeKey!, value)?.startTime,
        toTime: getDefaultTime(activeKey!, value)?.endTime,
      }).then(res => ({
        ...res,
        costFrom: res.costFrom?.filter(c => !isNil(c.charges)).sort((a, b) => b.charges! - a.charges!),
      }));
    },
    {
      ready: !!value,
      refreshDeps: [value],
    }
  );

  const handleCoordinateChange = useCallback((nextCoordinate: MapCoordinate) => {
    setCoordinate(nextCoordinate);
    setPointModalOpen(true);
  }, []);

  const handleUnityClick = useCallback(
    (detail: UnityClickDetail) => {
      if (enableCoordinatePicker) {
        const clickedCoordinate = getMapCoordinate(detail.data);
        if (clickedCoordinate) {
          handleCoordinateChange(clickedCoordinate);
          return;
        }
      }

      const modelName = detail?.data?.name?.trim();
      if (!modelName) return;
      setOverviewRegionName(modelName);
      setOverviewModalOpen(true);
    },
    [enableCoordinatePicker, handleCoordinateChange]
  );

  useEffect(() => registerUnityClickHandler(handleUnityClick), [handleUnityClick, registerUnityClickHandler]);

  const MapLayer = mapMode === 'unityMap' ? UnityMap : MapView;
  const coordinateTag =
    enableCoordinatePicker && loadMap ? (
      <Tag className={styles.coordinateTag}>{coordinate ? formatCoordinate(coordinate) : '点击地图获取经纬度'}</Tag>
    ) : null;

  const renderCenterStandardScreen = useMemo(() => {
    return (
      <div className={styles.standard_no_map_scroll}>
        <div className={styles.content_layer}>
          <div className={styles.center_left_box} style={{ flex: !loadMap ? 1 : undefined }}>
            {!loadMap && <CenterTopBox />}
            <LeftBox />
          </div>
          <div className={styles.center_right_box} style={{ flex: !loadMap ? 1 : undefined }}>
            {!loadMap && <CenterBottomBox />}
            <RightBox />
          </div>
        </div>
      </div>
    );
  }, [loadMap]);

  return (
    <ShareDataUnityEnergyCockpitMapContext.Provider
      value={{
        scaleData,
        timeResolution: activeKey,
        dateValue: value,
        setActiveKey,
        setValue,
        configDetail,
        energyCostData,
        energyCostDataLoading,
        energyUsageExpanded,
        setEnergyUsageExpanded,
        energyUsageContentVisible,
        overviewRegionName,
      }}
    >
      {isStandardSize ? (
        loadMap ? (
          <div
            className={`${styles.inner_center} ${styles.inner_center_standard}`}
            style={{ background: !loadMap ? '#111D2C' : 'transparent' }}
          >
            <MapLayer
              className={`${isFullScreen ? styles.fullMap : styles.map}`}
              onCoordinateChange={enableCoordinatePicker ? handleCoordinateChange : undefined}
            >
              <CenterTopBox className={styles.centerBox} />
              <CenterBottomBox className={styles.centerBottomBox} />
            </MapLayer>
            {coordinateTag}
          </div>
        ) : (
          <>{renderCenterStandardScreen}</>
        )
      ) : (
        <div
          className={classNames(styles.inner_center, !loadMap ? styles.inner_center_no_map : '')}
          style={{ background: !loadMap ? '#111D2C' : 'transparent' }}
        >
          {loadMap && (
            <MapLayer
              className={styles.map}
              onCoordinateChange={enableCoordinatePicker ? handleCoordinateChange : undefined}
            ></MapLayer>
          )}
          {coordinateTag}
          {loadMap && (
            <div className={styles.center_center_box}>
              <CenterTopBox className="centerBox" />
              <CenterBottomBox className="centerBottomBox" />
            </div>
          )}
          <div
            className={classNames(styles.content_layer, !loadMap ? styles.content_layer_no_map : '')}
            style={{ justifyContent: !loadMap ? 'center' : undefined, gap: !loadMap ? 8 : undefined }}
          >
            <div
              className={classNames(
                styles.center_left_box,
                !energyUsageExpanded && !isStandardSize && loadMap ? styles.center_left_box_collapsed : '',
                !sidebarTransitionEnabled ? styles.center_left_box_no_transition : '',
                !loadMap ? styles.noMapWidth : '',
                !loadMap ? styles.noMapColumn : ''
              )}
              onTransitionEnd={handleLeftSidebarTransitionEnd}
            >
              {!loadMap && <CenterTopBox />}
              <LeftBox />
            </div>
            <div
              className={classNames(
                styles.center_right_box,
                !isStandardSize && loadMap ? styles.center_right_box_collapsed : '',
                !loadMap ? styles.noMapWidth : '',
                !loadMap ? styles.noMapColumn : ''
              )}
            >
              {!loadMap && <CenterBottomBox />}
              <RightBox />
            </div>
          </div>
        </div>
      )}
      <Modal
        title="用能概览"
        open={overviewModalOpen}
        width={1240}
        footer={null}
        centered
        className={styles.overviewModal}
        onCancel={() => {
          setOverviewModalOpen(false);
        }}
        getContainer={() => document.getElementById('full_screen_box') ?? document.body}
      >
        <EnergyOverviewModal />
      </Modal>
      {enableCoordinatePicker && (
        <MapPointModal open={pointModalOpen} coordinate={coordinate} onClose={() => setPointModalOpen(false)} />
      )}
    </ShareDataUnityEnergyCockpitMapContext.Provider>
  );
};

export default UnityEnergyCockpitMap;
